import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { listCreditPacks } from '@/config/pricing';
import { formatUsd } from '@/lib/ai-tools/format';
import { saveStudioDraft } from '@/lib/ai-tools/resume';
import { apiPost } from '@/lib/api-client';
import { track } from '@/lib/track';
import { cn } from '@/lib/utils';
import { m } from '@/paraglide/messages.js';

/**
 * Packs shown right under the Generate button when the balance can't cover the
 * render. One tap parks the studio form, sends the visitor to Stripe and brings
 * them back to a render that starts by itself. The smallest pack that covers
 * the missing credits is the highlighted one.
 */
export function StudioPacks({
  slug,
  missing,
  standardCredits,
  unit,
  getDraft,
}: {
  slug: string;
  /** Credits the visitor is short of for the selected render. */
  missing: number;
  /** Credits of one standard render; the pack lines count these. */
  standardCredits: number;
  unit: 'video' | 'image';
  /** The form to restore after payment, with photos already uploaded; null aborts the purchase. */
  getDraft: () => Promise<unknown | null>;
}) {
  const packs = [...listCreditPacks()].sort((a, b) => a.priceInCents - b.priceInCents);
  const best = packs.find((pack) => pack.credits >= missing) ?? packs[packs.length - 1];

  useEffect(() => {
    track('tool_packs_shown', { tool: slug, missing });
  }, [slug]);

  const checkout = useMutation({
    mutationFn: async (productId: string) => {
      track('tool_pack_click', { tool: slug, pack: productId });
      const draft = await getDraft();
      if (draft === null) throw new Error(m['directory.ai_tools.studio.need_photos']());
      saveStudioDraft(slug, draft);
      return apiPost<{ checkout_url: string }>('/api/payment/checkout', {
        product_id: productId,
        redirect: `/tools/${slug}?credits=added#studio`,
      });
    },
    onSuccess: (data, productId) => {
      if (data?.checkout_url) {
        track('tool_checkout_started', { tool: slug, pack: productId, source: 'studio' });
        window.location.assign(data.checkout_url);
      } else {
        toast.error(m['directory.ai_tools.error.generic']());
      }
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="space-y-3 rounded-xl border border-primary/30 bg-primary/5 p-4">
      <div>
        <p className="text-sm font-semibold text-foreground">
          {m['directory.ai_tools.studio.checkout_title']({ missing: missing.toLocaleString() })}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">{m['directory.ai_tools.studio.checkout_hint']()}</p>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        {packs.map((pack) => {
          const count = Math.floor(pack.credits / standardCredits);
          const busy = checkout.isPending && checkout.variables === pack.productId;
          return (
            <Button
              key={pack.productId}
              type="button"
              variant={pack === best ? 'default' : 'outline'}
              disabled={checkout.isPending}
              onClick={() => checkout.mutate(pack.productId)}
              className={cn('h-auto flex-col items-start gap-0.5 whitespace-normal px-3 py-2.5 text-left')}
            >
              {busy ? (
                <span className="flex items-center gap-2 text-sm font-semibold">
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  {m['directory.ai_tools.packs.redirecting']()}
                </span>
              ) : (
                <>
                  <span className="text-base font-bold">{formatUsd(pack.priceInCents)}</span>
                  <span className="text-xs font-normal opacity-90">
                    {unit === 'image'
                      ? m['directory.ai_tools.studio.pack_line_image']({ credits: pack.credits.toLocaleString(), count })
                      : m['directory.ai_tools.studio.pack_line_video']({ credits: pack.credits.toLocaleString(), count })}
                  </span>
                </>
              )}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
